<template>
  <component v-bind:is="tag" class="image-aside-slide">
    <aside
      v-bind:class="imageWrapperClass"
      v-bind:style="aspectRatioPaddingStyle"
      v-on:click="followImageLink()"
    >
      <img
        v-lazy="imageSrc"
        v-bind:class="aspectRatioBoxClass"
        v-bind:alt="imageAlt"
        v-bind:width="imageWidth"
        v-bind:height="imageHeight"
        ref="imageAside"
      >
    </aside>
    <div
      v-bind:class="imageDescriptionClass"
      ref="imageAsideDescription"
    >
      <slot></slot>
    </div>
  </component>
</template>

<script>
import styles from '@/styles/_variables.scss'

export default {
  name: 'ImageAsideSlide',
  props: {
    'image-src': {type: String},
    'image-alt': {
      type: String,
      default: null
    },
    'image-width': {
      type: Number,
      default: null
    },
    'image-height': {
      type: Number,
      default: null
    },
    'image-link': {
      type: Object,
      default: null
    },
    'preferred-image-position': {
      type: String,
      default: 'left'
    },
    'auto-full-screen': {
      type: Boolean,
      default: true
    },
    'tag': {
      type: String,
      default: 'section'
    }
  },
  data () {
    return {
      fullScreenImage: (
        window.innerWidth <= parseInt(styles.tabletMediaBreakpoint))
    }
  },
  mounted () {
    this.$refs.imageAside.onload = this.updateFullScreenMode
  },
  created () {
    window.addEventListener('resize', this.updateFullScreenMode)
  },
  destroyed () {
    window.removeEventListener('resize', this.updateFullScreenMode)
  },
  computed: {
    imageWrapperClass: function () {
      return [
        'image-aside-wrapper', this.fullScreenClass, this.aspectRatioBoxClass]
    },
    imageDescriptionClass: function () {
      const asideLeftClass = {
        'text-aside-left': (
          this.preferredImagePosition === 'right' &&
          !this.fullScreenImage)
      }
      return ['image-aside-description', asideLeftClass, this.fullScreenClass]
    },
    fullScreenClass: function () {
      return {'full-screen': this.fullScreenImage}
    },
    areImageDimensionsKnown: function () {
      return (this.imageWidth && this.imageHeight)
    },
    aspectRatioPadding: function () {
      if (!this.areImageDimensionsKnown) {
        return 0
      }
      let padding = 100 * this.imageHeight / this.imageWidth
      return (this.fullScreenImage) ? padding : padding / 2
    },
    aspectRatioBoxClass: function () {
      return {'aspect-ratio-box': this.areImageDimensionsKnown}
    },
    aspectRatioPaddingStyle: function () {
      const paddingStyle = {
        paddingTop: `${this.aspectRatioPadding.toFixed(2)}%`
      }
      return (this.areImageDimensionsKnown) ? paddingStyle : null
    }
  },
  methods: {
    updateFullScreenMode: function () {
      const isSmallScreen = (
        window.innerWidth <= parseInt(styles.tabletMediaBreakpoint)
      )
      if (this.autoFullScreen) {
        // would compute blocks' heights as if the image was on half page
        this.fullScreenImage = false
        // wait util DOM is reloaded and then compute blocks' heights
        this.$nextTick(function () {
          const imageHeight = this.$refs.imageAside.clientHeight
          const textHeight = this.$refs.imageAsideDescription.clientHeight
          this.fullScreenImage = (imageHeight < textHeight) || isSmallScreen
        })
      } else {
        this.fullScreenImage = isSmallScreen
      }
    },
    followImageLink: function () {
      const isSmallScreen = (
        window.innerWidth <= parseInt(styles.tabletMediaBreakpoint)
      )
      if (isSmallScreen && this.imageLink) {
        this.$router.push(this.imageLink)
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @mixin half-page-flex-item {
    flex-basis: 50%;
    &.full-screen { flex-basis: 100%; }
  }
  .image-aside-slide {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    @media only print {
      display: block;
      overflow: auto;
    }
    @media only print and (orientation: landscape) {
      padding: 1em 0;
    }

    & > .image-aside-wrapper {
      @include half-page-flex-item;
      @media only print { page-break-inside: avoid; }
      @media only print and (orientation: landscape) {
        float: left;
        width: 50%;
        margin-bottom: 0.75cm;
      }
      &.aspect-ratio-box {
        position: relative;
        height: 0;
        overflow: hidden;
      }
      & > img {
        display: block;
        max-width: 100%;
        height: auto;
        margin: 0 auto;  /* center image if it is too small */
        @media only print {
          max-width: 80%;
          max-height: 50vh;
        }
        &.aspect-ratio-box {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: 100%;
        }
      }
    }
    & > .image-aside-description {
      padding: 1.5em 0;
      @include half-page-flex-item;
      @media only print and (orientation: landscape) {
        padding: 0;
      }
      &.text-aside-left {
        order: -1;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/the-content/utils/ImageAsideSlide.vue